import type {
    Author, Category, ContentfulSector, NormalizedContent, NormalizedPersistedSectorsState, Product,
    SearchableTicker, SimplePageContent
} from "@tmi/types/entities";
import { openDatabase, runTransaction, wrapCacheError } from "./cache-database";
import type { StoreName } from "./cache-schema";
import { createStateStore, createStore } from "./cache-store";

export type { CacheStore, StateStore } from "./cache-store";
export { CacheError, CacheClosedError, CacheOpenError } from "./cache-database";
export type { StoreName } from "./cache-schema";

/**
 * The one readiness handle. Every store method awaits the open internally; call `cache.ready()` explicitly
 * only where you want a timeout around the open (see stores/SectorHistory.ts). Rejects with CacheClosedError
 * once a newer build has upgraded the database (a reload is in flight) — callers must treat that as "stop
 * quietly", see the Wave 2 rules.
 */
export const cache = {
    ready: (): Promise< void > => openDatabase().then( () => undefined ),
    transaction
};

/**
 * `runTransaction` behind the same contract as every store method: a failure is always a CacheError carrying
 * `cause` — a failed request, or a throw from `work` — and a CacheError subclass passes through unchanged (F-M3).
 */
function transaction< R >(
    stores: readonly StoreName[],
    mode: IDBTransactionMode,
    work: ( tx: IDBTransaction ) => R | Promise< R >
): Promise< R > {
    return wrapCacheError( `transaction over [ ${ stores.join( ', ' ) } ]`, () => runTransaction( stores, mode, work ) );
}

export const articles      = createStore< NormalizedContent, 'contentfulId' >( 'articles', 'contentfulId' );
export const authors       = createStore< Author, 'id' >( 'authors', 'id' );
export const bonusReports  = createStore< NormalizedContent, 'contentfulId' >( 'reports', 'contentfulId' );
export const categories    = createStore< Category, 'id' >( 'categories', 'id' );
export const products      = createStore< Product, 'id' >( 'products', 'id' );
export const sectors       = createStore< ContentfulSector, 'contentfulId' >( 'sectors', 'contentfulId' );
export const sectorHistory = createStore< NormalizedPersistedSectorsState, 'dt' >( 'history', 'dt' );
export const simplePages   = createStore< SimplePageContent, 'id' >( 'pages', 'id' );
export const syncState     = createStateStore( 'sync_state' );
export const tickers       = createStore< SearchableTicker, 'id' >( 'tickers', 'id' );
export const tradeAlerts   = createStore< NormalizedContent, 'contentfulId' >( 'alerts', 'contentfulId' );
export const webinars      = createStore< NormalizedContent, 'contentfulId' >( 'webinars', 'contentfulId' );
